import '@babel/polyfill';

import { patchUigsAtta } from '@asset/js/uigsAtta';
import UIGS from '@tencent/search-uigs';
import axios from 'axios';
import Vue from 'vue';
import VueLazyload from 'vue-lazyload';

import { createApp } from './entry-base';

function detectDarkTheme() {
  const el = document.documentElement;
  const scheme = el.getAttribute('data-color-scheme');
  const darkreaderScheme = el.getAttribute('data-darkreader-scheme');
  if (scheme) {
    return scheme === 'dark';
  }
  return darkreaderScheme === 'dark';
}

function syncTheme() {
  const root = document.documentElement;
  if (detectDarkTheme()) {
    root.setAttribute('data-theme', 'dark');
  } else if (root.hasAttribute('data-theme')) {
    root.removeAttribute('data-theme');
  }
}

const getTheme = () => (detectDarkTheme() ? 'dark' : 'light');
const bootTime = Date.now();
// 首次加载只同步，不刷新（避免页面刚进来就被重载）
let prevTheme = getTheme();
syncTheme();

// 终端就地切换日/夜时，容器深色引擎动态重绘不干净，样式会错乱。
// 监听到主题真正变化后：先同步主题 + 用目标主题色整屏遮罩盖住中间乱态，
// 再整页刷新重建页面，降低切换时的闪烁感。
let reloadTimer = null;
let lastReloadTime = 0;
const MIN_RELOAD_INTERVAL = 3000; // 最小刷新间隔，防止死循环
// 页面加载初期容器引擎可能异步注入/调整 scheme 属性，属正常初始化，不触发刷新
const RELOAD_IGNORE_MS = 2000;

function themeSwitchMask(dark) {
  const mask = document.createElement('div');
  mask.id = '__theme_switch_mask__';
  mask.style.cssText =    'position:fixed;left:0;top:0;width:100%;height:100%;z-index:2147483647;'
    + `background:${dark ? '#1a1a1a' : '#fff'};opacity:0;transition:opacity .15s ease;`;
  document.documentElement.appendChild(mask);
  // 下一帧再淡入，确保过渡生效
  requestAnimationFrame(() => {
    mask.style.opacity = '1';
  });
}

function scheduleReload() {
  if (Date.now() - bootTime < RELOAD_IGNORE_MS) return; // 初始化阶段不做整页刷新
  const now = Date.now();
  clearTimeout(reloadTimer);
  if (now - lastReloadTime > MIN_RELOAD_INTERVAL) {
    reloadTimer = setTimeout(() => {
      lastReloadTime = Date.now();
      location.reload();
    }, 160); // 遮罩淡入盖住中间态后再刷新
  }
}

if (typeof MutationObserver !== 'undefined') {
  new MutationObserver(() => {
    const curTheme = getTheme();
    if (curTheme !== prevTheme) {
      prevTheme = curTheme;
      syncTheme();
      if (Date.now() - bootTime >= RELOAD_IGNORE_MS) {
        themeSwitchMask(curTheme === 'dark');
        scheduleReload();
      }
    } else {
      // 属性有变动但主题未变化（如引擎重复注入），仅重新同步
      syncTheme();
    }
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-color-scheme', 'data-darkreader-scheme'],
  });
}

require('es6-promise').polyfill();

Vue.use(VueLazyload, {
  preLoad: 1.7,
  attempt: 1,
  listenEvents: ['click', 'scroll', 'wheel', 'mousewheel', 'resize', 'animationend', 'transitionend'],
});

Vue.prototype.$http = axios.create();
Vue.prototype.$uigs = new UIGS({ uigs_productid: 'wap_video' });
Vue.prototype.$uigsKd = new UIGS({ uigs_productid: 'wap_video_kd' });

const { NODE_ENV } = process.env;
const isDev = NODE_ENV === 'development';
// const isProd = NODE_ENV === 'production';
if (isDev) {
  Vue.config.devtools = true;
}
// else if (isProd && window.aegis && window.aegis.error) {
//   Vue.config.errorHandler = function (err, vm, info) {
//     const { uuid = '' } = window.UIGS_PARA || {};
//     const uuidStr = uuid ? `\nuuid: ${uuid}` : '';
//     window.aegis.error(`Error: ${err.toString()}\nStack: ${err.stack}\nInfo: ${info}${uuidStr}`);
//   };
// }

// a global mixin that calls `asyncData` when a route component's params change
Vue.mixin({
  beforeRouteUpdate(to, from, next) {
    const { asyncData } = this.$options;
    const context = {
      origin: window.location.origin,
    };
    if (asyncData) {
      asyncData({
        context,
        store: this.$store,
        route: to,
      })
        .then(next)
        .catch(next);
    } else {
      next();
    }
  },
});

const { app, router, store } = createApp();

// HACK: 增加快照判断参数，处理抓取页面快照重定向问题
const isSnapshot = window.location.pathname.indexOf('hc_video_tab_') > -1;

// prime the store with server-initialized state.
// the state is determined during SSR and inlined in the page markup.
if (window.__INITIAL_STATE__) {
  isSnapshot && delete window.__INITIAL_STATE__.route;
  store.replaceState(window.__INITIAL_STATE__);
}

// store 就绪后统一给 UIGS 上报打补丁；是否上报由七彩石 switch_config.sendUigsLog / sendAttaLog 控制（'on'/'off'）
patchUigsAtta(() => {
  const config = (store.state && store.state.common && store.state.common.$config) || {};
  return {
    sendUigsLog: config.sendUigsLog !== 'off', // 默认开启，保持既有 UIGS 上报
    sendAttaLog: config.sendAttaLog === 'on', // 默认关闭，仅显式开启时镜像 ATTA
  };
});

// wait until router has resolved all async before hooks
// and async components...
router.onReady(() => {
  // Add router hook for handling asyncData.
  // Doing it after initial route is resolved so that we don't double-fetch
  // the data that we already have. Using router.beforeResolve() so that all
  // async components are resolved.
  router.beforeResolve((to, from, next) => {
    if (to.query.isclient === 1) {
      return next();
    }
    const matched = router.getMatchedComponents(to);
    const prevMatched = router.getMatchedComponents(from);
    let diffed = false;
    const activated = matched.filter((c, i) => diffed || (diffed = prevMatched[i] !== c));
    const asyncDataHooks = activated.map(c => c.asyncData).filter(_ => _);
    if (!asyncDataHooks.length) {
      return next();
    }

    // bar.start();
    // 对所有匹配的路由组件调用 `asyncData()`
    const context = {
      origin: window.location.origin,
    };
    Promise.all(asyncDataHooks.map(hook => hook({ context, store, route: to })))
      .then(() => {
        // bar.finish();
        next();
      })
      .catch(next);
  });

  // actually mount to DOM
  app.$mount('#videoApp');
});
